import { IEntityPickerFetcher, EpickTreeConstants } from './entityPickerFetcher';
import { IEpickNode } from './EpickTree';
import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import { IEntityPickerTreeModel, getTreeNode, defaultEpickTreeModel } from './defaultEpickTreeModel';
import { DecisionsTypePickerSettings } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/DecisionsTypePickerSettings';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData/DataPair';
import {
  EntityTypeInfo,
  newEntityTypeInfo,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/EntityTypeInfo';
import { EntityPickerItem } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/EntityPickerItem';
import { splitNameAndType } from './decisionsTypeTreeModel';

/**
 * DecisionsTypeDropdownTreeModel
 *
 * TODO sasha: simplify
 */
export function dropdownTreeModel(
  settings: DecisionsTypePickerSettings,
  dataFetcher: IEntityPickerFetcher,
  nodes?: IEpickNode[]
): IEntityPickerTreeModel {
  const base: IEntityPickerTreeModel = defaultEpickTreeModel(settings, dataFetcher);

  const getRootNodes = async (): Promise<ITreeNode<IEpickNode>[]> => {
    let roots: ITreeNode<IEpickNode>[] = [];
    if (base.getFavoritesRootNode) {
      roots.push(...(await base.getFavoritesRootNode()));
    }
    if (base.getRecentRootNode) {
      roots.push(...(await base.getRecentRootNode()));
    }

    // when no roots, undefined is there
    roots = roots.filter(n => n !== undefined);

    if (settings.TypeNodesInfo) {
      roots.push(
        getTreeNode({
          id: EpickTreeConstants.userDefinedTypesFolderId,
          name: EpickTreeConstants.userdefinedTypesFolderName,
          expandable: true,
          order: 7,
        } as IEpickNode)
      );
    }

    const cur = base.getCurrentFolderNode ? await base.getCurrentFolderNode() : [];
    // show current folder only if it has childNodes
    if (cur && cur[0] && cur[0].data?.expandable) {
      cur[0].data.order = 8;
      cur[0].data.expanded = false;
      cur[0].isExpanded = false;
      roots.push(cur[0]);
    }

    // additional nodes after Current Folder
    if (nodes) {
      roots.push(...nodes.map(n => getTreeNode(n)));
    }
    for (let rootNode of roots) {
      if (Array.isArray(rootNode.childNodes)) {
        rootNode.childNodes.map(node => {
          const [name, type] = splitNameAndType(node.data?.name ?? '');
          node.name = name;
          node.description = type;
          return node;
        });
      }
    }
    return roots;
  };

  const getSubNodes = async (nodeId: string): Promise<ITreeNode<IEpickNode>[]> => {
    const subs: ITreeNode<IEpickNode>[] = [];
    if (nodeId === EpickTreeConstants.userDefinedTypesFolderId) {
      if (settings.TypeNodesInfo) {
        settings.TypeNodesInfo.forEach((ut: DataPair) => {
          subs.push(
            getTreeNode({
              id: ut.Name,
              name: ut.OutputValue,
              expandable: true,
            } as IEpickNode)
          );
        });
      }
      return subs;
    }

    if (nodes && nodes.filter(n => nodeId === n.id).length) {
      return [];
    }

    if (settings.TypeNodesInfo) {
      const userType: DataPair = settings.TypeNodesInfo.filter((dp: DataPair) => dp.Name === nodeId)[0];
      if (userType) {
        const etype: EntityTypeInfo = newEntityTypeInfo();
        etype.EntityTypeName = userType.Name;
        const entities: EntityPickerItem[] = await dataFetcher.fetchEntitiesOfTypes([etype]);
        if (entities) {
          subs.push(
            ...entities.map(e =>{
              const [name, type] = splitNameAndType(e.EntityName);
              return getTreeNode({
                id: e.Id,
                name: name,
                pickable: true,
                favoriteType: e.FavoriteType,
                canBeFav: true,
                title: type,
              } as IEpickNode)
            })
          );
        }
        return subs;
      }
    }

    const items = await base.getSubNodes(nodeId);
    return items.map(node => {
      const [name, type] = splitNameAndType(node.data?.name ?? '');
      node.name = name;
      node.description = type;
      return node;
    });
  };

  return { ...base, getSubNodes, getRootNodes };
}
